/**
 * Speisekarten-Import (Spec 06, F3 Review-UI): Upload/Text -> KI-Parse -> menschlicher
 * Review (nie Auto-Commit) -> transaktionaler Katalog-Commit.
 *
 * Der Review ist das Herzstueck: extrahierte Felder editierbar, Konfidenz-/Sanity-Hinweise
 * sichtbar (gelb), Allergene per LMIV-Toggle, Pflicht-Bestaetigung „Allergene geprueft"
 * vor dem Commit. Beim Commit wird der (ggf. korrigierte) Stand als parsedPayload gesendet.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, toApiError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { openAuthenticatedFile, fileToBase64 } from "../lib/files";
import type { CatalogCategory, CatalogItem, CommitSummary, MenuCard, MenuImport, ParsedMenu, SanityFlag } from "../ui/types";
import { PageHeader, Icon, ErrorNote } from "../ui/components";
import { parseGermanEurosToCents } from "../ui/format";

// LMIV-Hauptallergene + Diät-Flags: EINE gemeinsame Quelle für alle Flächen (ui/lmiv).
import { LMIV, DIETARY as DIET } from "../ui/lmiv";

/** Sprachname fuer die Erkennungs-Meldung („die englische Version von …"). */
const LANG_NAME: Record<string, string> = {
  en: "englische",
  it: "italienische",
  fr: "französische",
  es: "spanische",
  de: "deutsche",
};

// M3-Fix: hartes Client-Größenlimit (Spiegel des Server-Limits MAX_BYTES_PER_FILE = 8 MB).
// Übergroße Dateien werden gar nicht erst Base64-kodiert/hochgeladen (kein 413-Roundtrip).
const MAX_FILE_BYTES = 8 * 1024 * 1024;

/**
 * Zeigt IMMER die reale Fehlermeldung (Server-`message` bzw. der geworfene Fehler) —
 * keine erfundenen/ausgedachten Texte. Nur wenn wirklich keine Meldung vorliegt, greift
 * ein neutraler, ehrlicher Fallback plus (falls vorhanden) der rohe Fehlercode.
 */
function importError(message: string | null | undefined, code?: string | null): string {
  if (message && message.trim()) return message.trim();
  if (code && code.trim()) return `Fehler: ${code.trim()}`;
  return "Analyse fehlgeschlagen.";
}

// Kanonische Anzeige: deutsches Format mit Komma-Dezimaltrenner ("8,90").
function centsToEuros(c: number | null): string {
  return c == null ? "" : (c / 100).toFixed(2).replace(".", ",");
}
// Parsen ueber den geteilten Format-Util (deutsches Format: Komma-Dezimal, optional Punkt-Tausender)
// statt naivem parseFloat — sonst wuerde "1.500" faelschlich zu 1,50 € statt 1.500 €.
function eurosToCents(input: string): number | null {
  return parseGermanEurosToCents(input);
}

/**
 * C1-Fix: Preis-Eingabe im Review-Schritt.
 * Waehrend des Tippens wird der ROH-String gehalten (kein reformat-on-keystroke) — sonst
 * schrieb der onChange bei jedem Tastendruck den zu Cents geparsten & zurueckformatierten
 * Wert ins Feld, setzte den Caret zurueck und mehrstellige Eingaben zerstoerten sich
 * ("8,90" -> "8.01"). Normalisiert/committet wird erst beim Verlassen des Felds (onBlur).
 * Muster gespiegelt vom Angebots-Builder (useBufferedField).
 */
function PriceInput({
  cents,
  priceDisplay,
  onCommit,
}: {
  cents: number | null;
  priceDisplay: string | null | undefined;
  onCommit: (cents: number | null) => void;
}) {
  const canonical = centsToEuros(cents);
  const [raw, setRaw] = useState(canonical);
  const focused = useRef(false);
  // Externe Aenderungen (z. B. KI-Reparse) uebernehmen — aber nie waehrend der Nutzer tippt.
  useEffect(() => {
    if (!focused.current) setRaw(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical]);
  return (
    <input
      className="input pr-7 text-right"
      inputMode="decimal"
      placeholder={priceDisplay ?? "0,00"}
      value={raw}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        focused.current = false;
        const c = eurosToCents(raw);
        onCommit(c);
        setRaw(centsToEuros(c));
      }}
    />
  );
}

type Step = "upload" | "parsing" | "review" | "done";
type SourceTab = "file" | "text";

// Datei-Größe menschenlesbar (deutsches Komma) für die Datei-Chip-Anzeige.
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1).replace(".", ",")} MB`;
}

// Pillen-Tab im App-Stil (gespiegelt von catalog.tsx) — für „PDF oder Foto" / „Text einfügen".
function tabClass(active: boolean): string {
  return `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-label-md text-label-md transition-colors ${
    active
      ? "bg-primary text-on-primary"
      : "bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest dark:bg-surface-variant/30 dark:text-secondary-fixed-dim"
  }`;
}

/**
 * Schritt-Indikator (Redesign 9a): sitzt im Upload-Schritt im Kartenkopf, in den
 * übrigen Schritten über dem Inhalt. Ein Indikator, immer nur ein aktiver Schritt.
 */
function Stepper({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2 font-label-md text-label-md">
      {(["upload", "review", "done"] as const).map((s, i) => {
        const active = step === s || (s === "review" && step === "parsing");
        const done = (s === "upload" && step !== "upload") || (s === "review" && step === "done");
        return (
          <div key={s} className="flex items-center gap-2">
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full ${
                done
                  ? "bg-primary text-on-primary"
                  : active
                    ? "bg-primary/15 text-primary ring-1 ring-primary"
                    : "bg-surface-container-high text-on-surface-variant dark:bg-surface-variant/30"
              }`}
            >
              {done ? <Icon name="check" className="text-[16px]" /> : i + 1}
            </span>
            <span className={active || done ? "text-on-background dark:text-inverse-on-surface" : "text-on-surface-variant"}>
              {s === "upload" ? "Hochladen" : s === "review" ? "Prüfen" : "Fertig"}
            </span>
            {i < 2 && <span className="mx-1 h-px w-8 bg-outline-variant/50" />}
          </div>
        );
      })}
    </div>
  );
}

export default function MenuImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("upload");
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [text, setText] = useState("");
  // Redesign 9a: Datei/Text als Tabs statt gestapelt.
  const [srcTab, setSrcTab] = useState<SourceTab>("file");
  // Ein einziges verstecktes File-Input, per Ref aus Dropzone UND den Recovery-Aktionen (9b) ausgelöst.
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Drag-anywhere: sobald irgendwo auf der Seite eine Datei überzogen wird, aktiviert sich der Upload-Bereich.
  const [pageDragActive, setPageDragActive] = useState(false);
  const dragDepth = useRef(0);

  // Nimmt nur PDF/Bild-Dateien innerhalb des Größenlimits an - für Klick UND Drop (behebt die
  // frühere Inkonsistenz: Drop filterte, die Klick-Auswahl nicht). M3-Fix: Größe VOR jeder
  // Base64-Verarbeitung prüfen, damit übergroße Dateien gar nicht erst kodiert/hochgeladen werden.
  function acceptFiles(list: FileList | null) {
    const typed = Array.from(list ?? []).filter((f) => f.type === "application/pdf" || f.type.startsWith("image/"));
    const tooBig = typed.filter((f) => f.size > MAX_FILE_BYTES);
    const ok = typed.filter((f) => f.size <= MAX_FILE_BYTES);
    if (tooBig.length > 0) {
      setError(`Datei zu groß: Das Limit beträgt 8 MB (${tooBig.map((f) => f.name).join(", ")}).`);
      if (ok.length === 0) return; // nichts Gültiges dabei -> abbrechen, keine Base64-Verarbeitung
    } else {
      setError(null);
    }
    if (ok.length > 0) {
      setFiles(ok);
      setSrcTab("file");
      // Neue Datei nach einem Fehlversuch -> zurück in den normalen Grundzustand (9a).
      setParseFailed(false);
    }
  }

  // Drag-anywhere (Redesign-Wunsch): ein Drag über die GANZE Seite aktiviert den Upload-Bereich
  // und schaltet auf den Datei-Tab. Das window-Drop fängt Ablegen an jeder Stelle ab, damit der
  // Browser die Datei nicht einfach öffnet. Nur im Upload-Schritt aktiv.
  useEffect(() => {
    if (step !== "upload") return;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    function onEnter(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setPageDragActive(true);
      setSrcTab("file");
    }
    function onOver(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault(); // erlaubt Drop und verhindert das Standard-Öffnen der Datei
    }
    function onLeave() {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setPageDragActive(false);
    }
    function onDrop(e: DragEvent) {
      e.preventDefault();
      dragDepth.current = 0;
      setPageDragActive(false);
      if (hasFiles(e)) acceptFiles(e.dataTransfer?.files ?? null);
    }
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
      dragDepth.current = 0;
    };
    // acceptFiles nutzt nur Setter (stabil) — keine veraltete State-Closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const [job, setJob] = useState<MenuImport | null>(null);
  const [menu, setMenu] = useState<ParsedMenu | null>(null);
  // H-08: nach einem fehlgeschlagenen Parse explizit "Erneut analysieren" (Dateien/Text
  // bleiben erhalten) und "Manuell im Katalog anlegen" anbieten statt einer Sackgasse.
  const [parseFailed, setParseFailed] = useState(false);
  const [allergensConfirmed, setAllergensConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<CommitSummary | null>(null);
  // P1/P2 (docs/KONZEPT-SPEISEKARTEN.md): Ziel-Karte des Imports. Die Karte entsteht erst BEIM
  // Commit (verworfene Imports hinterlassen keine leeren Karten). "replace" (P2) archiviert
  // alles auf der Karte, was der neue Import nicht enthaelt. Deep-Link aus der Karten-
  // Verwaltung: /katalog/import?cardId=<id>&mode=replace waehlt die Karte vor.
  const [searchParams] = useSearchParams();
  const preselectCardId = searchParams.get("cardId");
  const preselectReplace = searchParams.get("mode") === "replace";
  const [cardMode, setCardMode] = useState<"create" | "append" | "replace" | "translate" | "none">(
    preselectCardId ? (preselectReplace ? "replace" : "append") : "create",
  );
  const [cardName, setCardName] = useState("");
  const [cardKind, setCardKind] = useState<"food" | "drinks" | "mixed">("food");
  const [appendCardId, setAppendCardId] = useState(preselectCardId ?? "");
  // Mehrsprachigkeit Weg A: Zielsprache des Uebersetzungs-Imports (Default: erkannte
  // Quellsprache der PDF, sobald der Parse durch ist).
  const [translateLang, setTranslateLang] = useState("en");
  const cardsQ = useApi<MenuCard[]>("/api/menu-cards");
  const existingCards = cardsQ.data ?? [];
  // Uebersetzungs-Erkennung: Katalog + Kategorien fuer den Preis-Fingerprint je Karte.
  const detectItemsQ = useApi<CatalogItem[]>("/api/catalog/items");
  const detectCatsQ = useApi<CatalogCategory[]>("/api/catalog/categories");
  // Vorschlag einmal abgelehnt/uebernommen -> nicht erneut aufdraengen.
  const [detectionDismissed, setDetectionDismissed] = useState(false);

  /**
   * Uebersetzungs-Erkennung ueber den PREIS-FINGERABDRUCK: Sprachversionen derselben Karte
   * haben (fast) identische Preislisten - sprachunabhaengig und robust gegen Layout-Nuancen.
   * Vergleicht die Preis-Multimenge der geparsten Karte mit jeder bestehenden Karte
   * (Overlap / groessere Menge). Nur ein VORSCHLAG - der Betreiber entscheidet.
   */
  const translationCandidate = useMemo((): { card: MenuCard; score: number } | null => {
    if (!menu || existingCards.length === 0) return null;
    const parsedPrices: number[] = [];
    for (const s of menu.sections) for (const it of s.items) if (it.priceCents != null && it.priceCents > 0) parsedPrices.push(it.priceCents);
    if (parsedPrices.length < 5) return null; // zu wenig Signal
    const cardByCategory = new Map((detectCatsQ.data ?? []).map((c) => [c.id, c.cardId ?? null]));
    const pricesByCard = new Map<string, number[]>();
    for (const it of detectItemsQ.data ?? []) {
      if (it.archivedAt != null || it.priceCents <= 0 || !it.categoryId) continue;
      const cardId = cardByCategory.get(it.categoryId);
      if (!cardId) continue;
      pricesByCard.set(cardId, [...(pricesByCard.get(cardId) ?? []), it.priceCents]);
    }
    const toMultiset = (arr: number[]) => {
      const m = new Map<number, number>();
      for (const p of arr) m.set(p, (m.get(p) ?? 0) + 1);
      return m;
    };
    const parsedSet = toMultiset(parsedPrices);
    let best: { card: MenuCard; score: number } | null = null;
    for (const card of existingCards) {
      const cardPrices = pricesByCard.get(card.id) ?? [];
      if (cardPrices.length < 5) continue;
      const cardSet = toMultiset(cardPrices);
      let overlap = 0;
      for (const [price, n] of parsedSet) overlap += Math.min(n, cardSet.get(price) ?? 0);
      const score = overlap / Math.max(parsedPrices.length, cardPrices.length);
      if (score > (best?.score ?? 0)) best = { card, score };
    }
    // Schwelle: >=80 % Preisuebereinstimmung; bei fremdsprachiger Quelle reicht das klar,
    // bei gleicher Sprache waere es eher ein Duplikat -> trotzdem anzeigen (Betreiber entscheidet).
    return best && best.score >= 0.8 ? best : null;
  }, [menu, existingCards, detectItemsQ.data, detectCatsQ.data]);

  const flags: SanityFlag[] = job?.confidence?.flags ?? [];
  const flagFor = useMemo(() => {
    const map = new Map<string, SanityFlag[]>();
    for (const f of flags) {
      const key = `${f.sectionIndex}:${f.itemIndex ?? "s"}`;
      map.set(key, [...(map.get(key) ?? []), f]);
    }
    return map;
  }, [flags]);

  async function startParse() {
    setError(null);
    setParseFailed(false);
    if (files.length === 0 && text.trim() === "") {
      setError("Bitte eine PDF/Foto hochladen oder Text einfügen.");
      return;
    }
    setStep("parsing");
    try {
      const encoded = await Promise.all(files.map(fileToBase64));
      const sourceKind = files.length > 0 ? (files[0]!.type === "application/pdf" ? "pdf" : "image") : "text";
      const res = await apiFetch("/api/menu-imports", {
        method: "POST",
        body: JSON.stringify({
          sourceKind,
          sourceLang: "de",
          files: encoded.length ? encoded : undefined,
          textContent: text.trim() || undefined,
        }),
      });
      const body = (await res.json().catch(() => null)) as { data?: MenuImport; message?: string; error?: string } | null;
      if (!res.ok || !body?.data) {
        // Reale Server-Meldung anzeigen (nicht erfinden).
        setError(importError(body?.message, body?.error));
        setParseFailed(true);
        setStep("upload");
        return;
      }
      setJob(body.data);
      setMenu(body.data.parsedPayload);
      setDetectionDismissed(false);
      // Mehrsprachigkeit: erkannte Quellsprache als Uebersetzungs-Zielsprache vorbelegen
      // (die EN-PDF ist auf Englisch -> lang='en').
      if (body.data.sourceLang && /^[a-z]{2}$/.test(body.data.sourceLang) && body.data.sourceLang !== "de") {
        setTranslateLang(body.data.sourceLang);
      }
      // P1: Karten-Namen aus dem Dateinamen vorbelegen (ohne Endung, gekuerzt) - nur wenn der
      // Betreiber noch nichts eingetippt hat.
      setCardName((prev) => {
        if (prev.trim()) return prev;
        const base = files[0]?.name.replace(/\.[a-z0-9]+$/i, "").trim();
        return (base || `Speisekarte ${new Date().toLocaleDateString("de-DE")}`).slice(0, 120);
      });
      setStep("review");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setParseFailed(true);
      setStep("upload");
    }
  }

  function patchItem(si: number, ii: number, patch: Partial<ParsedMenu["sections"][0]["items"][0]>) {
    // L6-Fix: funktionaler Updater statt Closure-Snapshot. Mehrere schnelle Folge-Updates
    // (z. B. Allergen-Bestaetigungen kurz hintereinander) gingen sonst verloren, weil jeder
    // Aufruf vom selben `menu`-Snapshot ausging und das letzte setMenu die vorigen ueberschrieb.
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si]!.items[ii] = { ...next.sections[si]!.items[ii]!, ...patch };
      return next;
    });
  }
  function patchSection(si: number, patch: Partial<ParsedMenu["sections"][0]>) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si] = { ...next.sections[si]!, ...patch };
      return next;
    });
  }
  function patchVariant(si: number, vi: number, patch: Partial<{ label: string; priceCents: number }>) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      const variants = [...(next.sections[si]!.menuVariants ?? [])];
      variants[vi] = { ...variants[vi]!, ...patch };
      next.sections[si] = { ...next.sections[si]!, menuVariants: variants };
      return next;
    });
  }
  function addVariant(si: number) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si] = {
        ...next.sections[si]!,
        menuVariants: [...(next.sections[si]!.menuVariants ?? []), { label: "mit Weinbegleitung", priceCents: 0 }],
      };
      return next;
    });
  }
  function removeVariant(si: number, vi: number) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      const variants = [...(next.sections[si]!.menuVariants ?? [])];
      variants.splice(vi, 1);
      next.sections[si] = { ...next.sections[si]!, menuVariants: variants };
      return next;
    });
  }
  function removeItem(si: number, ii: number) {
    // L6-Fix: funktionaler Updater (siehe patchItem) — race-sicher gegen schnelle Folge-Updates.
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si]!.items.splice(ii, 1);
      return next;
    });
  }

  // Sammel-Bestaetigung: alle Positionen mit ungeklaerten Allergenen (null) in EINEM
  // Update auf "geprueft, keine LMIV-Hauptallergene" ([]) setzen. Spart bei grossen Karten
  // das Einzel-Bestaetigen. Bereits gesetzte Positionen bleiben unveraendert.
  function confirmAllUnresolvedAllergens() {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      for (const s of next.sections) {
        for (const it of s.items) {
          if (it.allergens === null) it.allergens = [];
        }
      }
      return next;
    });
  }

  async function commit() {
    if (!job || !menu) return;
    // P1 (Speisekarten): Ziel-Karte validieren, BEVOR der Commit losgeht.
    if (cardMode === "create" && !cardName.trim()) {
      setError("Bitte einen Namen für die neue Karte angeben – oder Modus »Ohne Karte« wählen.");
      return;
    }
    if ((cardMode === "append" || cardMode === "replace" || cardMode === "translate") && !appendCardId) {
      setError("Bitte eine bestehende Karte auswählen – oder Modus »Ohne Karte« wählen.");
      return;
    }
    // P2: Ersetzen ist ein gewichtiger Eingriff — explizit bestätigen (mit Klartext-Folgen).
    if (cardMode === "replace") {
      const target = existingCards.find((k) => k.id === appendCardId);
      const ok = confirm(
        `Karte „${target?.name ?? "?"}" durch diesen Import ERSETZEN?\n\nPositionen und Kategorien, die im neuen Import fehlen, werden archiviert (nicht gelöscht – Angebote und Pakete behalten ihre Referenzen).`,
      );
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    try {
      const card =
        cardMode === "create"
          ? { mode: "create" as const, name: cardName.trim(), kind: cardKind }
          : cardMode === "translate"
            ? { mode: "translate" as const, cardId: appendCardId, lang: translateLang }
            : cardMode === "append" || cardMode === "replace"
              ? { mode: cardMode, cardId: appendCardId }
              : undefined;
      const res = await apiFetch(`/api/menu-imports/${job.id}/commit`, {
        method: "POST",
        body: JSON.stringify({ parsedPayload: menu, ...(card ? { card } : {}) }),
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: CommitSummary };
      setSummary(body.data);
      setStep("done");
    } catch (e) {
      // Reale Fehlermeldung durchreichen (nicht erfinden).
      setError(importError(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  const itemCount = menu?.sections.reduce((n, s) => n + s.items.length, 0) ?? 0;
  // Tri-State (H-02): jede Position mit ungeklaerten Allergenen (null) muss einzeln
  // aufgeloest werden (Codes waehlen oder "keine Allergene" bestaetigen), sonst kein Commit.
  const unresolvedAllergens = menu?.sections.reduce((n, s) => n + s.items.filter((i) => i.allergens === null).length, 0) ?? 0;

  return (
    <>
      <PageHeader
        title="Speisekarte importieren"
        subtitle="PDF oder Foto hochladen – die KI erkennt Gerichte, Preise und Allergene. Sie prüfen, dann landen sie im Katalog."
        actions={
          <button type="button" className="btn-secondary" onClick={() => navigate("/katalog")}>
            <Icon name="arrow_back" className="text-[18px]" /> Zum Katalog
          </button>
        }
      />

      {/* Schritt-Indikator: im Upload-Schritt sitzt er im Kartenkopf (9a), sonst darüber. */}
      {step !== "upload" && (
        <div className="mb-6">
          <Stepper step={step} />
        </div>
      )}

      {/* Ein einziges verstecktes File-Input für Dropzone + Recovery-Aktionen. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        multiple
        className="hidden"
        onChange={(e) => acceptFiles(e.target.files)}
      />

      {/* Drag-anywhere-Overlay: erscheint, sobald irgendwo auf der Seite eine Datei gezogen wird. */}
      {step === "upload" && pageDragActive && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-8 backdrop-blur-[3px]">
          <div className="pointer-events-none flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-primary bg-surface-container-lowest/95 px-16 py-12 text-center shadow-xl dark:bg-inverse-surface/95">
            <Icon name="upload_file" className="text-5xl text-primary" />
            <p className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
              Datei hier ablegen
            </p>
            <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              PDF, JPG oder PNG · max. 8 MB je Datei
            </p>
          </div>
        </div>
      )}

      {/* -------- Upload (9a Grundzustand / 9b Fehlerzustand) -------- */}
      {step === "upload" && (
        <div className="card overflow-hidden">
          {/* Kartenkopf: Untertitel + Stepper (9a) */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-outline-variant/30 bg-surface-container-low px-6 py-4 dark:border-outline/30 dark:bg-surface-variant/10">
            <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              PDF oder Foto hochladen – die KI erkennt Gerichte, Preise und Allergene.
            </p>
            <Stepper step={step} />
          </div>

          <div className="space-y-5 p-6">
            {parseFailed ? (
              /* 9b Fehlerzustand: Fehler sitzt an der Datei, alle Optionen an EINER Stelle, kein Doppel-CTA. */
              <div className="space-y-4">
                {files.length > 0 && (
                  <div className="flex items-center gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3 dark:border-outline/30 dark:bg-surface-variant/10">
                    <Icon name="description" className="text-2xl text-on-surface-variant" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                        {files[0]!.name}
                      </p>
                      <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                        {files[0]!.type === "application/pdf" ? "PDF" : "Bild"} · {formatFileSize(files[0]!.size)}
                      </p>
                    </div>
                  </div>
                )}
                <div className="rounded-xl border border-red-300/70 bg-red-50/70 p-5 dark:border-red-400/30 dark:bg-red-400/10">
                  <div className="flex items-start gap-3">
                    <Icon name="error" className="text-2xl text-red-600 dark:text-red-400" />
                    <div className="space-y-1">
                      <p className="font-body-md text-body-md font-semibold text-red-800 dark:text-red-200">
                        Analyse fehlgeschlagen
                      </p>
                      <p className="font-body-sm text-body-sm text-red-700/90 dark:text-red-200/80">
                        {error ?? "Analyse fehlgeschlagen."}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button type="button" className="btn-primary" onClick={() => void startParse()}>
                      <Icon name="refresh" className="text-[18px]" /> Erneut analysieren
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()}>
                      <Icon name="image" className="text-[18px]" /> Anderes Foto wählen
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => navigate("/katalog")}>
                      <Icon name="edit" className="text-[18px]" /> Manuell im Katalog anlegen
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* 9a Grundzustand: Datei/Text als Tabs, eine einzige Aktion unten. */
              <>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={tabClass(srcTab === "file")} onClick={() => setSrcTab("file")}>
                    <Icon name="upload_file" className="text-[16px]" /> PDF oder Foto
                  </button>
                  <button type="button" className={tabClass(srcTab === "text")} onClick={() => setSrcTab("text")}>
                    <Icon name="notes" className="text-[16px]" /> Text einfügen
                  </button>
                </div>

                {srcTab === "file" ? (
                  <>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                      onDrop={(e) => { e.preventDefault(); setDragActive(false); acceptFiles(e.dataTransfer.files); }}
                      className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-12 text-center transition-colors ${
                        dragActive || pageDragActive
                          ? "border-primary bg-primary/5"
                          : "border-outline-variant/60 bg-surface-container-low hover:border-primary/50 dark:border-outline/40 dark:bg-surface-variant/10"
                      }`}
                    >
                      <Icon name="upload_file" className="text-4xl text-primary" />
                      <div>
                        <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                          Speisekarte hierher ziehen
                        </p>
                        <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                          PDF, JPG oder PNG · mehrere Seiten möglich · max. 8 MB je Datei
                        </p>
                      </div>
                      <span className="btn-secondary pointer-events-none mt-1">Datei wählen</span>
                    </div>

                    {files.length > 0 && (
                      <div className="space-y-2">
                        {files.map((f, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3 dark:border-outline/30 dark:bg-surface-variant/10"
                          >
                            <Icon
                              name={f.type === "application/pdf" ? "picture_as_pdf" : "image"}
                              className="text-2xl text-primary"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                                {f.name}
                              </p>
                              <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                                {f.type === "application/pdf" ? "PDF" : "Bild"} · {formatFileSize(f.size)} ·{" "}
                                <span className="text-primary">Bereit zur Analyse</span>
                              </p>
                            </div>
                            <button
                              type="button"
                              className="btn-ghost !px-2 text-on-surface-variant"
                              title="Datei entfernen"
                              onClick={() => setFiles((prev) => prev.filter((_, k) => k !== i))}
                            >
                              <Icon name="close" className="text-[18px]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <textarea
                    className="input min-h-[160px]"
                    placeholder="Speisekarte als Text einfügen (z. B. aus einer E-Mail) …"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                )}

                {/* Nicht-fatale Hinweise (z. B. Datei zu groß, leere Eingabe) — inline, kein Seiten-Banner. */}
                {error && (
                  <p className="flex items-center gap-1.5 font-body-sm text-body-sm text-red-600 dark:text-red-400">
                    <Icon name="error" className="text-[16px]" /> {error}
                  </p>
                )}

                {/* Eine einzige Aktion unten + Sicherheitshinweis (9a). */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-outline-variant/30 pt-5 dark:border-outline/30">
                  <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                    In den Katalog übernommen wird erst nach deiner Prüfung.
                  </p>
                  <button type="button" className="btn-primary" onClick={() => void startParse()}>
                    <Icon name="auto_awesome" className="text-[18px]" /> Karte analysieren
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* -------- Parsing -------- */}
      {step === "parsing" && (
        <div className="card flex flex-col items-center justify-center gap-4 py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
          <p className="font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">
            Die KI liest Ihre Speisekarte … das dauert meist unter einer Minute.
          </p>
        </div>
      )}

      {/* -------- Review -------- */}
      {step === "review" && menu && (
        <div className="space-y-5">
          {error && <ErrorNote message={error} />}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-outline-variant/30 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
            <div className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              <span className="font-semibold text-on-background dark:text-inverse-on-surface">{itemCount} Positionen</span> in{" "}
              {menu.sections.length} Kategorien erkannt.
              {menu.sections.filter((sec) => sec.menuPriceCents != null).length > 0 && (
                <span>
                  {" "}· davon {menu.sections.filter((sec) => sec.menuPriceCents != null).length}{" "}
                  {menu.sections.filter((sec) => sec.menuPriceCents != null).length === 1 ? "Menü" : "Menüs"} mit Gesamtpreis
                </span>
              )}
              {job?.aiUsage && (
                <span className="ml-2">· Analysekosten ≈ {(job.aiUsage.costEurCents / 100).toFixed(2)} €</span>
              )}
            </div>
            {flags.some((f) => f.level === "warn") && (
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-100 px-2.5 py-1 font-label-md text-label-md text-yellow-800 dark:bg-yellow-400/15 dark:text-yellow-200">
                <Icon name="warning" className="text-[16px]" /> Gelb markierte Felder bitte prüfen
              </span>
            )}
          </div>

          {/* Backlog #41: Original-Dateien (R2) - nur die, fuer die tatsaechlich ein r2Key persistiert wurde. */}
          {job && job.sourceFiles.some((f) => f.r2Key) && (
            <div className="flex flex-wrap items-center gap-2 font-body-sm text-body-sm text-on-surface-variant">
              <span>Originaldateien:</span>
              {job.sourceFiles.map((f, i) =>
                f.r2Key ? (
                  <button
                    key={i}
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg bg-surface-container-high px-2.5 py-1 font-label-md text-label-md text-primary hover:underline dark:bg-surface-variant/30"
                    onClick={() => void openAuthenticatedFile(`/api/menu-imports/${job.id}/files/${i}`)}
                  >
                    <Icon name="visibility" className="text-[15px]" /> Datei {i + 1} ansehen
                  </button>
                ) : null,
              )}
            </div>
          )}

          {/* M2-Fix: Empty-State statt leerer Liste, wenn die KI keine Gerichte erkannt hat -
              inkl. zweier sichtbarer Recovery-Optionen statt einer stillen Sackgasse. */}
          {itemCount === 0 && (
            <div className="card flex flex-col items-center gap-4 py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-container-high text-on-surface-variant dark:bg-surface-variant/30">
                <Icon name="search_off" className="text-4xl" />
              </div>
              <h2 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
                Keine Gerichte erkannt
              </h2>
              <p className="max-w-md font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">
                In dieser Datei bzw. diesem Text wurden keine Gerichte gefunden. Erneut analysieren, eine andere Quelle wählen oder manuell im Katalog anlegen.
              </p>
              <div className="mt-2 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { setStep("upload"); setFiles([]); setText(""); setJob(null); setMenu(null); }}
                >
                  <Icon name="refresh" className="text-[18px]" /> Anderes Foto versuchen
                </button>
                <button type="button" className="btn-secondary" onClick={() => navigate("/katalog")}>
                  <Icon name="edit" className="text-[18px]" /> Manuell im Katalog anlegen
                </button>
              </div>
            </div>
          )}

          {menu.sections.map((section, si) => (
            <div key={si} className="card overflow-hidden">
              <div className="border-b border-outline-variant/30 bg-surface-container-low px-5 py-3 dark:border-outline/30 dark:bg-surface-variant/10">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
                    {section.title}
                    {section.menuPriceCents != null && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-0.5 align-middle font-label-md text-label-md text-primary">
                        <Icon name="restaurant_menu" className="text-[14px]" /> Menü · Preis p. P.
                      </span>
                    )}
                  </h3>
                  {section.menuPriceCents == null ? (
                    <button
                      type="button"
                      className="btn-ghost !px-2 font-label-md text-label-md text-on-surface-variant"
                      title="Diese Sektion als Menü mit einem Gesamtpreis pro Person behandeln (Gänge ohne Einzelpreise)"
                      onClick={() => patchSection(si, { menuPriceCents: 0, menuVariants: section.menuVariants ?? [] })}
                    >
                      <Icon name="restaurant_menu" className="text-[16px]" /> Als Menü mit Gesamtpreis
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-ghost !px-2 font-label-md text-label-md text-on-surface-variant"
                      title="Menü auflösen: Positionen wieder als Einzelgerichte mit eigenen Preisen behandeln"
                      onClick={() => patchSection(si, { menuPriceCents: null })}
                    >
                      <Icon name="close" className="text-[16px]" /> Menü auflösen
                    </button>
                  )}
                </div>
                {section.menuPriceCents != null && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-label-md text-label-md text-on-surface-variant">Menüpreis pro Person</span>
                      <div className="relative w-[140px]">
                        <PriceInput
                          cents={section.menuPriceCents}
                          priceDisplay={section.menuPriceDisplay}
                          onCommit={(cents) => patchSection(si, { menuPriceCents: cents ?? 0 })}
                        />
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant">€</span>
                      </div>
                    </div>
                    {(section.menuVariants ?? []).map((variant, vi) => (
                      <div key={vi} className="flex flex-wrap items-center gap-2">
                        <input
                          className="input w-[220px]"
                          value={variant.label}
                          placeholder="Variante, z.B. mit Weinbegleitung"
                          onChange={(e) => patchVariant(si, vi, { label: e.target.value })}
                        />
                        <div className="relative w-[140px]">
                          <PriceInput cents={variant.priceCents} priceDisplay={null} onCommit={(cents) => patchVariant(si, vi, { priceCents: cents ?? 0 })} />
                          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant">€</span>
                        </div>
                        <button
                          type="button"
                          className="btn-ghost !px-2 text-on-surface-variant"
                          title="Variante entfernen"
                          onClick={() => removeVariant(si, vi)}
                        >
                          <Icon name="delete" className="text-[16px]" />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn-ghost !px-2 font-label-md text-label-md text-primary" onClick={() => addVariant(si)}>
                      <Icon name="add" className="text-[16px]" /> Preis-Variante
                    </button>
                    {(flagFor.get(`${si}:s`) ?? []).map((f, k) => (
                      <p key={k} className={`font-body-sm text-body-sm ${f.level === "warn" ? "text-yellow-800 dark:text-yellow-200" : "text-on-surface-variant"}`}>
                        {f.level === "warn" ? "⚠ " : "ℹ "}{f.message}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                {section.items.map((item, ii) => {
                  const itemFlags = (flagFor.get(`${si}:${ii}`) ?? []).filter(
                    (f) => !(section.menuPriceCents != null && f.field === "price" && f.level === "info"),
                  );
                  const warn = itemFlags.some((f) => f.level === "warn");
                  return (
                    <div key={ii} className={`p-4 ${warn ? "bg-yellow-50/60 dark:bg-yellow-400/5" : ""}`}>
                      <div className="flex items-start gap-3">
                        <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_140px]">
                          <div className="space-y-2">
                            <input
                              className="input font-semibold"
                              value={item.name}
                              onChange={(e) => patchItem(si, ii, { name: e.target.value })}
                            />
                            <textarea
                              className="input min-h-[40px] text-body-sm"
                              placeholder="Beschreibung (optional)"
                              value={item.description ?? ""}
                              onChange={(e) => patchItem(si, ii, { description: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            {section.menuPriceCents != null ? (
                              <div
                                className="rounded-lg bg-surface-container-high px-2.5 py-2 text-center font-body-sm text-body-sm text-on-surface-variant dark:bg-surface-variant/30"
                                title="Gänge eines Menüs tragen keinen Einzelpreis - der Preis steht am Menü."
                              >
                                Im Menüpreis
                              </div>
                            ) : (
                              <>
                                <div className="relative">
                                  <PriceInput
                                    cents={item.priceCents}
                                    priceDisplay={item.priceDisplay}
                                    onCommit={(cents) => patchItem(si, ii, { priceCents: cents })}
                                  />
                                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant">€</span>
                                </div>
                                {item.priceCents == null && item.priceDisplay && (
                                  <p className="text-right font-body-sm text-body-sm text-on-surface-variant/70">{item.priceDisplay}</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-ghost !px-2 text-on-surface-variant"
                          title="Position entfernen"
                          onClick={() => removeItem(si, ii)}
                        >
                          <Icon name="delete" className="text-[18px]" />
                        </button>
                      </div>

                      {/* Allergene (Tri-State H-02: null = ungeklaert, [] = geprueft keine, Codes = geprueft) */}
                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        {LMIV.map(({ code, label }) => {
                          const on = item.allergens?.includes(code) ?? false;
                          return (
                            <button
                              key={code}
                              type="button"
                              title={label}
                              onClick={() =>
                                patchItem(si, ii, {
                                  allergens: on
                                    ? (item.allergens ?? []).filter((a) => a !== code)
                                    : [...(item.allergens ?? []), code],
                                })
                              }
                              className={`rounded border px-1.5 py-0.5 font-label-md text-[11px] font-semibold transition-colors ${
                                on
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "border-outline-variant/50 text-on-surface-variant/60 hover:border-primary/40 dark:border-outline/40"
                              }`}
                            >
                              {code}
                            </button>
                          );
                        })}
                        {item.allergens === null && (
                          <button
                            type="button"
                            title="Diese Position hat keine erkannte Allergenangabe. Bestätigen, dass sie keine LMIV-Hauptallergene enthält — oder oben Codes anklicken."
                            onClick={() => patchItem(si, ii, { allergens: [] })}
                            className="rounded border border-amber-500/60 bg-amber-500/10 px-2 py-0.5 font-label-md text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-500/20 dark:text-amber-300"
                          >
                            Ungeklärt — „keine Allergene" bestätigen
                          </button>
                        )}
                        <span className="mx-1 w-px bg-outline-variant/40" />
                        {DIET.map(({ key, label }) => {
                          const on = item.dietFlags.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() =>
                                patchItem(si, ii, {
                                  dietFlags: on ? item.dietFlags.filter((d) => d !== key) : [...item.dietFlags, key],
                                })
                              }
                              className={`rounded border px-2 py-0.5 font-label-md text-[11px] transition-colors ${
                                on
                                  ? "border-green-600 bg-green-600/10 text-green-700 dark:text-green-300"
                                  : "border-outline-variant/50 text-on-surface-variant/60 hover:border-green-500/40 dark:border-outline/40"
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Sanity-Hinweise */}
                      {itemFlags.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {itemFlags.map((f, k) => (
                            <p
                              key={k}
                              className={`flex items-center gap-1.5 font-body-sm text-body-sm ${
                                f.level === "warn" ? "text-yellow-700 dark:text-yellow-300" : "text-on-surface-variant/70"
                              }`}
                            >
                              <Icon name={f.level === "warn" ? "warning" : "info"} className="text-[14px]" />
                              {f.message}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* P1 (Speisekarten): Ziel-Karte des Imports — bewusst HIER im Review (der Betreiber
              sieht erst, WAS erkannt wurde, und entscheidet dann, wohin es gehört). */}
          {itemCount > 0 && (
            <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-4 dark:border-outline/30 dark:bg-inverse-surface">
              <h4 className="mb-3 flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                <Icon name="menu_book" className="text-[16px]" /> Ziel-Karte
              </h4>
              {/* Uebersetzungs-Erkennung (Preis-Fingerprint): nur ein VORSCHLAG mit Ein-Klick-
                  Uebernahme - Radios darunter bleiben immer frei uebersteuerbar. */}
              {translationCandidate && !detectionDismissed && cardMode !== "translate" && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
                  <Icon name="translate" className="text-[18px] text-primary" />
                  <span className="min-w-0 flex-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    Diese Karte scheint die {LANG_NAME[job?.sourceLang ?? ""] ?? "fremdsprachige"} Version von{" "}
                    <strong>„{translationCandidate.card.name}"</strong> zu sein ({Math.round(translationCandidate.score * 100)} %
                    Preisübereinstimmung).
                  </span>
                  <span className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      className="btn-primary !px-3 !py-1.5"
                      onClick={() => {
                        setCardMode("translate");
                        setAppendCardId(translationCandidate.card.id);
                        setDetectionDismissed(true);
                      }}
                    >
                      Als Übersetzung übernehmen
                    </button>
                    <button type="button" className="btn-secondary !px-3 !py-1.5" onClick={() => setDetectionDismissed(true)}>
                      Nein, eigenständige Karte
                    </button>
                  </span>
                </div>
              )}
              {!translationCandidate && job?.sourceLang && job.sourceLang !== "de" && cardMode !== "translate" && existingCards.length > 0 && (
                <p className="mb-3 rounded-lg bg-surface-container-high/50 px-3 py-2 font-body-sm text-body-sm text-on-surface-variant dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
                  <Icon name="translate" className="mr-1 align-[-3px] text-[15px]" />
                  Fremdsprachige Karte erkannt ({(job.sourceLang ?? "").toUpperCase()}). Falls das die Übersetzung einer
                  bestehenden Karte ist, wähle unten „Übersetzung zu bestehender Karte".
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm">
                  <input type="radio" name="cardMode" checked={cardMode === "create"} onChange={() => setCardMode("create")} />
                  Neue Karte anlegen
                </label>
                <label className={`flex items-center gap-2 font-body-sm text-body-sm ${existingCards.length === 0 ? "opacity-40" : "cursor-pointer"}`}>
                  <input type="radio" name="cardMode" disabled={existingCards.length === 0} checked={cardMode === "append"} onChange={() => setCardMode("append")} />
                  Bestehende Karte ergänzen
                </label>
                <label className={`flex items-center gap-2 font-body-sm text-body-sm ${existingCards.length === 0 ? "opacity-40" : "cursor-pointer"}`}>
                  <input type="radio" name="cardMode" disabled={existingCards.length === 0} checked={cardMode === "replace"} onChange={() => setCardMode("replace")} />
                  Bestehende Karte ersetzen
                </label>
                <label className={`flex items-center gap-2 font-body-sm text-body-sm ${existingCards.length === 0 ? "opacity-40" : "cursor-pointer"}`}>
                  <input type="radio" name="cardMode" disabled={existingCards.length === 0} checked={cardMode === "translate"} onChange={() => setCardMode("translate")} />
                  Übersetzung zu bestehender Karte
                </label>
                <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm">
                  <input type="radio" name="cardMode" checked={cardMode === "none"} onChange={() => setCardMode("none")} />
                  Ohne Karte
                </label>
              </div>
              {cardMode === "create" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input className="input max-w-[340px]" placeholder="Name der Karte, z. B. Mittagskarte" value={cardName} onChange={(e) => setCardName(e.target.value)} />
                  <select
                    className="input max-w-[200px]"
                    value={cardKind}
                    onChange={(e) => setCardKind(e.target.value as "food" | "drinks" | "mixed")}
                    title="Die Karten-Art bestimmt Art und MwSt. neuer Positionen (Getränkekarte → Getränk, 19 %)."
                  >
                    <option value="food">Speisekarte</option>
                    <option value="drinks">Getränkekarte</option>
                    <option value="mixed">Gemischt (Speisen + Getränke)</option>
                  </select>
                </div>
              )}
              {(cardMode === "append" || cardMode === "replace" || cardMode === "translate") && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select className="input max-w-[340px]" value={appendCardId} onChange={(e) => setAppendCardId(e.target.value)}>
                    <option value="">Karte wählen …</option>
                    {existingCards.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name} ({k.itemCount ?? 0} Positionen)
                      </option>
                    ))}
                  </select>
                  {cardMode === "translate" && (
                    <select className="input max-w-[180px]" value={translateLang} onChange={(e) => setTranslateLang(e.target.value)} title="Sprache dieser Karte (= Zielsprache der Übersetzung)">
                      <option value="en">Englisch</option>
                      <option value="it">Italienisch</option>
                      <option value="fr">Französisch</option>
                      <option value="es">Spanisch</option>
                    </select>
                  )}
                </div>
              )}
              {cardMode === "translate" && (
                <p className="mt-2 rounded-lg bg-primary/5 px-3 py-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                  <Icon name="translate" className="mr-1 align-[-3px] text-[15px]" />
                  Übersetzungs-Modus: Die Texte werden per Reihenfolge den Positionen der Ziel-Karte zugeordnet.
                  Preise, Allergene und Struktur der Ziel-Karte bleiben unangetastet; Preisabweichungen werden gemeldet.
                </p>
              )}
              {cardMode === "replace" && (
                <p className="mt-2 rounded-lg bg-error/5 px-3 py-2 font-body-sm text-body-sm text-error">
                  <Icon name="warning" className="mr-1 align-[-3px] text-[15px]" />
                  Ersetzen archiviert alle Positionen und Kategorien dieser Karte, die im neuen Import fehlen.
                  Angebote und Pakete behalten ihre Referenzen; betroffene Pakete werden markiert.
                </p>
              )}
              <p className="mt-2 font-body-sm text-[12px] text-on-surface-variant/70">
                Karten gruppieren den Katalog (Mittags-/Abend-/Getränkekarte) und lassen sich später einzeln erneuern.
              </p>
            </div>
          )}

          {/* Commit-Leiste — M2-Fix: bei 0 Positionen ausgeblendet, der Empty-State übernimmt. */}
          {itemCount > 0 && (
          <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-outline-variant/30 bg-surface-container-lowest/95 p-4 shadow-lg backdrop-blur dark:border-outline/30 dark:bg-inverse-surface/95">
            {cardMode === "translate" ? (
              /* Mehrsprachigkeit: der Uebersetzungs-Commit schreibt KEINE Allergene/Preise -
                 die LMIV-Pflichtbestaetigung entfaellt hier bewusst (Ziel-Karte bleibt wie sie ist). */
              <p className="flex items-start gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                <Icon name="translate" className="mt-0.5 text-[18px]" />
                Es werden nur Übersetzungen auf die gewählte Karte geschrieben — Preise und Allergene (LMIV)
                der Ziel-Karte bleiben unverändert gültig.
              </p>
            ) : (
            <label className="flex cursor-pointer items-start gap-2 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={allergensConfirmed}
                onChange={(e) => setAllergensConfirmed(e.target.checked)}
              />
              <span>
                Ich habe die <strong>Allergene (LMIV)</strong> und Preise geprüft. Sie sind rechtlich
                verbindlich für den Betrieb.
                {unresolvedAllergens > 0 && (
                  <span className="mt-1 block font-semibold text-amber-700 dark:text-amber-300">
                    {unresolvedAllergens} Position{unresolvedAllergens === 1 ? "" : "en"} mit ungeklärten
                    Allergenen — pro Position bestätigen oder rechts alle auf einmal.
                  </span>
                )}
              </span>
            </label>
            )}
            <div className="flex flex-wrap gap-3">
              {cardMode !== "translate" && unresolvedAllergens > 0 && (
                <button
                  type="button"
                  className="btn-secondary"
                  title="Setzt alle noch ungeklärten Positionen auf geprüft ohne LMIV-Hauptallergene. Einzeln gesetzte Codes bleiben erhalten."
                  onClick={confirmAllUnresolvedAllergens}
                >
                  Alle {unresolvedAllergens} als „keine Allergene“ bestätigen
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={() => navigate("/katalog")}>
                Abbrechen
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={
                  itemCount === 0 ||
                  busy ||
                  (cardMode === "translate" ? !appendCardId : !allergensConfirmed || unresolvedAllergens > 0)
                }
                onClick={() => void commit()}
              >
                {busy
                  ? "Übernimmt …"
                  : cardMode === "translate"
                    ? `${itemCount} Übersetzungen übernehmen`
                    : `${itemCount} Positionen in den Katalog übernehmen`}
              </button>
            </div>
          </div>
          )}
        </div>
      )}

      {/* -------- Done -------- */}
      {step === "done" && summary && (
        <div className="card flex flex-col items-center gap-4 py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-400/15 dark:text-green-300">
            <Icon name="check" className="text-4xl" />
          </div>
          <h2 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
            Katalog aktualisiert
          </h2>
          <p className="max-w-md font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">
            {summary.mode === "translate" ? (
              <>
                {summary.translatedItems ?? 0} Positionen und {summary.translatedCategories ?? 0} Kategorien übersetzt ({(summary.lang ?? "").toUpperCase()})
                {(summary.priceMismatches ?? 0) > 0 && (
                  <span className="mt-1 block font-semibold text-amber-700 dark:text-amber-300">
                    {summary.priceMismatches} Preisabweichung{summary.priceMismatches === 1 ? "" : "en"} zwischen den Sprachversionen — bitte prüfen, ob die übersetzte Karte aktuell ist.
                  </span>
                )}
                {(summary.skipped ?? 0) > 0 && ` · ${summary.skipped} ohne Gegenstück übersprungen`}
              </>
            ) : (
              <>
            {summary.createdItems} neue Positionen angelegt
            {summary.updatedItems > 0 && `, ${summary.updatedItems} aktualisiert`}
            {summary.createdCategories > 0 && ` · ${summary.createdCategories} neue Kategorien`}
            {(summary.createdPackages ?? 0) > 0 && ` · ${summary.createdPackages} ${summary.createdPackages === 1 ? "Menü-Paket" : "Menü-Pakete"} angelegt`}
            {(summary.archivedItems ?? 0) > 0 && ` · ${summary.archivedItems} Positionen archiviert`}
            {(summary.archivedCategories ?? 0) > 0 && ` · ${summary.archivedCategories} Kategorien archiviert`}
            {cardMode === "create" && cardName.trim() && ` · Karte „${cardName.trim()}" angelegt`}
            {cardMode === "append" && appendCardId && ` · Karte „${existingCards.find((k) => k.id === appendCardId)?.name ?? "?"}" ergänzt`}
            {cardMode === "replace" && appendCardId && ` · Karte „${existingCards.find((k) => k.id === appendCardId)?.name ?? "?"}" ersetzt`}.
              </>
            )}
          </p>
          <div className="mt-2 flex gap-3">
            <button type="button" className="btn-secondary" onClick={() => { setStep("upload"); setFiles([]); setText(""); setJob(null); setMenu(null); setSummary(null); setAllergensConfirmed(false); setCardMode("create"); setCardName(""); setCardKind("food"); setAppendCardId(""); }}>
              Weitere Karte importieren
            </button>
            {/* CX-Audit 2026-07-17: gezielt zu den KI-importierten Positionen (Filter-Chip in
                catalog.tsx, abwählbar) statt unfokussiert in die Gesamtliste. */}
            <button type="button" className="btn-primary" onClick={() => navigate("/katalog?source=ai_import")}>
              Importierte Positionen ansehen
            </button>
          </div>
        </div>
      )}
    </>
  );
}
